<template>
  <div class="group-control" :class="`group-control--${size}`" @click.stop>
    <GroupChip
      v-for="group in visibleGroups"
      :key="group.id"
      :group="group"
      :size="size"
      :removable="!readonly"
      class="group-control__chip"
      @remove="onToggle(group.id, false)" />
    <v-tooltip v-if="overflowCount > 0" location="top" :text="overflowNames">
      <template #activator="{ props: tipProps }">
        <span v-bind="tipProps" class="group-control__overflow">+{{ overflowCount }}</span>
      </template>
    </v-tooltip>
    <v-menu v-if="!readonly" v-model="menuOpen" :close-on-content-click="false" location="bottom start">
      <template #activator="{ props: menuProps }">
        <button
          v-bind="menuProps"
          type="button"
          class="group-control__add"
          :class="`group-control__add--${size}`"
          aria-label="Assign group"
          @click.stop>
          <v-icon :size="size === 'md' ? 14 : 15">mdi-account-multiple-plus-outline</v-icon>
          <span v-if="size === 'md'">Assign group</span>
        </button>
      </template>
      <v-card min-width="250" class="group-control__menu">
        <div class="group-control__search">
          <v-icon size="14" class="group-control__search-icon">mdi-magnify</v-icon>
          <input
            ref="searchInput"
            v-model="search"
            type="text"
            class="group-control__search-input"
            placeholder="Filter groups..."
            aria-label="Filter groups"
            @keydown="onSearchKeydown" />
        </div>
        <div ref="listEl" class="group-control__list">
          <div
            v-for="(group, index) in filteredGroups"
            :key="group.id"
            :data-index="index"
            class="group-control__item"
            :class="{ 'group-control__item--active': index === highlightedIndex }"
            @mousedown.prevent
            @mousemove="highlightedIndex = index"
            @click.stop="onToggle(group.id, !assignedIds.has(group.id))">
            <span class="group-control__item-icon" :style="iconStyle(group)">
              <v-icon size="13">mdi-account-group</v-icon>
            </span>
            <span class="group-control__item-label">{{ group.name }}</span>
            <span class="group-control__item-meta">{{ group.memberCount }}</span>
            <v-icon v-if="assignedIds.has(group.id)" size="16" color="accent">mdi-check</v-icon>
          </div>
          <div v-if="!filteredGroups.length" class="group-control__empty">
            {{ groups.length ? "No groups match" : "No groups yet" }}
          </div>
        </div>
      </v-card>
    </v-menu>
  </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from "vue";
import { TAG_COLORS } from "@/constants/tagColors";
import { assignmentsService } from "@/services/assignmentsService";
import GroupChip from "@/components/dashboard/GroupChip.vue";
import { useAuthStore, useActionNeededStore, useGroupsStore } from "@/stores";

const props = defineProps({
  assignedGroups: { type: Array, default: () => [] },
  entityType: { type: String, required: true, validator: (v) => ["document", "order", "submission"].includes(v) },
  entityId: { type: [String, Number], required: true },
  size: { type: String, default: "sm", validator: (v) => ["sm", "md"].includes(v) },
});

const emit = defineEmits(["update:assignedGroups"]);

const authStore = useAuthStore();
const actionNeededStore = useActionNeededStore();
const groupsStore = useGroupsStore();

// Assignments are admin-managed for assigned-cases-only users (LAM-4850) — the
// backend 403s their mutations, so render the chips display-only.
const readonly = computed(() => actionNeededStore.assignedCasesOnly ?? authStore.userInfo?.assignedCasesOnly === true);

// A grid cell shows one chip, which then gets the column's whole leftover width
// and stays readable; the rest fold into "+N". Free-width surfaces show 3.
const maxVisible = computed(() => (props.size === "sm" ? 1 : 3));
const assignedIds = computed(() => new Set((props.assignedGroups || []).map((g) => g.id)));
const visibleGroups = computed(() => (props.assignedGroups || []).slice(0, maxVisible.value));
const hiddenGroups = computed(() => (props.assignedGroups || []).slice(maxVisible.value).map((g) => g.name));
const overflowCount = computed(() => hiddenGroups.value.length);
const overflowNames = computed(() => hiddenGroups.value.join(", "));

const menuOpen = ref(false);
const search = ref("");
const highlightedIndex = ref(-1);
const searchInput = ref(null);
const listEl = ref(null);

const groups = computed(() => groupsStore.groups || []);

const filteredGroups = computed(() => {
  const query = search.value.trim().toLowerCase();
  if (!query) return groups.value;
  return groups.value.filter((group) => group.name.toLowerCase().includes(query));
});

watch(menuOpen, (open) => {
  if (open) {
    groupsStore.fetchGroups().catch(() => {});
    resetHighlight();
    nextTick(() =>
      requestAnimationFrame(() => {
        pinListHeight();
        searchInput.value?.focus();
        requestAnimationFrame(() => searchInput.value?.focus());
      })
    );
  } else {
    search.value = "";
    highlightedIndex.value = -1;
    listEl.value?.style.removeProperty("min-height");
  }
});

watch([search, groups], () => {
  resetHighlight();
  if (menuOpen.value && !search.value) nextTick(pinListHeight);
});

function pinListHeight() {
  if (listEl.value) listEl.value.style.minHeight = `${listEl.value.offsetHeight}px`;
}

function resetHighlight() {
  highlightedIndex.value = filteredGroups.value.length ? 0 : -1;
}

function moveHighlight(step) {
  const count = filteredGroups.value.length;
  if (!count) return;
  const start = highlightedIndex.value < 0 && step < 0 ? 0 : highlightedIndex.value;
  const index = (start + step + count) % count;
  highlightedIndex.value = index;
  nextTick(() => listEl.value?.querySelector(`[data-index="${index}"]`)?.scrollIntoView({ block: "nearest" }));
}

function toggleHighlighted() {
  const group = filteredGroups.value[highlightedIndex.value];
  if (group) onToggle(group.id, !assignedIds.value.has(group.id));
}

function onSearchKeydown(event) {
  if (event.isComposing) return;
  const actions = {
    ArrowDown: () => moveHighlight(1),
    ArrowUp: () => moveHighlight(-1),
    Enter: toggleHighlighted,
    Escape: () => (menuOpen.value = false),
  };
  const action = actions[event.key];
  if (!action) return;
  event.preventDefault();
  event.stopPropagation();
  action();
}

function iconStyle(group) {
  const color = TAG_COLORS[group.color] || TAG_COLORS.grey;
  return { background: `color-mix(in srgb, ${color} 14%, white)`, color };
}

// The menu stays open for several picks, so a second toggle can fire before the first PUT
// returns. `pending` keeps the latest intent (the prop still holds the pre-request value),
// and `seq` drops responses that a newer request has already superseded.
let pending = null;
let rollback = null;
let seq = 0;

/** Optimistic write of the group owners only; people are left untouched server-side and the response is the truth. */
async function onToggle(groupId, assign) {
  const previous = pending || props.assignedGroups || [];
  let next;
  if (assign) {
    const group = groups.value.find((g) => g.id === groupId);
    if (!group || previous.some((g) => g.id === groupId)) return;
    next = [...previous, { id: group.id, name: group.name, color: group.color }];
  } else {
    next = previous.filter((g) => g.id !== groupId);
  }
  if (rollback === null) rollback = props.assignedGroups || [];
  pending = next;
  const mine = ++seq;
  emit("update:assignedGroups", next);
  try {
    const { assignedGroups } = await assignmentsService.setAssignees(
      props.entityType,
      props.entityId,
      null,
      next.map((g) => g.id)
    );
    if (mine !== seq) return;
    pending = null;
    rollback = null;
    emit("update:assignedGroups", assignedGroups || []);
  } catch {
    if (mine !== seq) return;
    const restore = rollback || [];
    pending = null;
    rollback = null;
    emit("update:assignedGroups", restore);
  }
}
</script>

<style lang="scss" scoped>
.group-control {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;

  // Nothing else bounds a table cell, so without this the chips set the column
  // width and a long group name stretches it past every other column. Matches
  // the `groups` width declared in constants/tableColumns.js.
  &--sm {
    max-width: 170px;
  }

  &--md {
    flex-wrap: wrap;
    row-gap: 6px;
  }
}

// Shrinks into whatever the add button and "+N" leave, ellipsizing the name.
.group-control__chip {
  min-width: 0;
  flex: 0 1 auto;
}

.group-control__overflow {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 6px;
  border-radius: 5px;
  background: $lh_color_snowy_white;
  font-family: $lh_font_family_dm_sans;
  font-size: 10.5px;
  font-weight: 700;
  color: $lh_color_blue_gray;
  flex-shrink: 0;
}

// Mirrors .assignees__add so the two add affordances read as the same control.
// .assignees has no gap and spaces its button with margin-left alone; this container
// has gap: 4px, so the margin is the remainder. Flex gap applies only between
// siblings, so an empty cell (button alone) sits 4px further left than the
// assignee's does - visible only when comparing the two empty states side by side.
.group-control__add {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border: 1px dashed rgba($lh_color_navy, 0.25);
  background: transparent;
  color: $lh_color_blue_gray;
  font-family: $lh_font_family_dm_sans;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: color $lh_transition_fast, border-color $lh_transition_fast, background $lh_transition_fast;

  &:hover {
    color: $lh_color_blue;
    border-color: rgba($lh_color_blue, 0.45);
    background: rgba($lh_color_blue, 0.04);
  }

  &--sm {
    width: 26px;
    height: 26px;
    padding: 0;
    flex: 0 0 auto;
    border-radius: 50%;
    margin-left: 3px;
  }

  &--md {
    height: 30px;
    padding: 0 12px 0 9px;
    border-radius: 15px;
    margin-left: 4px;
  }
}

.group-control__menu {
  display: flex;
  flex-direction: column;
  max-height: 320px;
  border-radius: 12px !important;
}

.group-control__search {
  display: flex;
  align-items: center;
  gap: 7px;
  flex: 0 0 auto;
  padding: 10px 12px 8px;
  border-bottom: 1px solid rgba($lh_color_navy, 0.08);
}

.group-control__search-icon {
  color: $lh_color_blue_gray;
  flex: 0 0 auto;
}

.group-control__search-input {
  flex: 1 1 auto;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: $lh_font_family_dm_sans;
  font-size: 13.5px;
  color: $lh_color_navy;

  &::placeholder {
    color: $lh_color_blue_gray;
  }
}

.group-control__list {
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: 8px;
}

.group-control__item {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 6px 10px;
  border-radius: 8px;
  cursor: pointer;
  font-family: $lh_font_family_dm_sans;
  transition: background $lh_transition_fast;

  &--active {
    background: $lh_color_snowy_white;
  }
}

.group-control__item-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 6px;
  flex-shrink: 0;
}

.group-control__item-label {
  font-size: 13.5px;
  font-weight: 500;
  color: $lh_color_navy;
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.group-control__item-meta {
  font-family: $lh_font_family_dm_sans;
  font-size: 11.5px;
  color: $lh_color_blue_gray;
  flex: 0 0 auto;
}

.group-control__empty {
  margin: auto;
  padding: 10px;
  font-size: 13px;
  color: $lh_color_blue_gray;
}
</style>
